/**
 * Analytics tracking configuration
 * Handles user behavior tracking and performance monitoring
 */

interface NetworkInformation {
  type?: string;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
}

import { logger } from './logger';
import { siteConfig } from '@/constant/config';
import { visitorService, type VisitorData } from '@/services/api/visitors';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    mixpanel?: {
      track: (event: string, properties?: Record<string, unknown>) => void;
    };
  }
}

/**
 * GA4 keeps at most this many event parameters per hit. Beyond it, parameters
 * are dropped silently and non-deterministically — no error, just `(not set)`
 * on a random subset of your own dimensions, differently on each hit.
 */
export const GA4_MAX_EVENT_PARAMS = 25;

/** Everything `buildEnrichmentParams` needs, passed in so it stays pure. */
export interface EnrichmentContext {
  /** `null` accepted so a not-yet-identified user can be passed straight in. */
  userId?: string | null;
  appVersion?: string;
  userSegment?: string;
  isReturningUser?: boolean;
  daysSinceFirstVisit?: number;
  scrollDepth?: number;
}

/**
 * The fixed parameters stamped onto every GA4 event.
 *
 * Deliberately small. This used to stamp 44 parameters before the caller's own
 * properties were counted, so on the heaviest events roughly a third of every
 * payload was being discarded — including the registered custom dimensions the
 * product actually queries (`tournament_type` was arriving `(not set)` on 42%
 * of `tournament_creation_attempt` hits). The caller's properties are the ones
 * that answer product questions, so the enrichment is what has to give.
 *
 * Two rules decide what survives:
 *   1. If GA4 collects it natively (page_location, page_referrer, geo, device,
 *      browser, language, ga_session_id, engagement_time_msec, event_timestamp)
 *      it is not repeated here — that spends a slot to duplicate a dimension
 *      the reports already have.
 *   2. If it is not registered as a custom dimension it cannot be queried at
 *      all, so it is not worth a slot. That removed the geo precision, the
 *      Network Information fields, the screen/viewport metrics and the UTM
 *      params (GA4 derives session traffic source from the landing URL).
 *
 * What is left is the handful GA4 cannot derive on its own. `scroll_depth`,
 * `user_segment`, `is_returning_user` and `days_since_first_visit` still need
 * registering as custom dimensions before they are queryable.
 *
 * Undefined values are omitted rather than sent, so a missing app_version does
 * not spend a parameter slot on `undefined`.
 */
export function buildEnrichmentParams(
  ctx: EnrichmentContext
): Record<string, unknown> {
  const params: Record<string, unknown> = {
    user_id: ctx.userId,
    app_version: ctx.appVersion,
    user_segment: ctx.userSegment,
    is_returning_user: ctx.isReturningUser,
    days_since_first_visit: ctx.daysSinceFirstVisit,
    scroll_depth: ctx.scrollDepth,
  };

  for (const key of Object.keys(params)) {
    if (params[key] === undefined || params[key] === null) delete params[key];
  }

  return params;
}

export interface AnalyticsEvent {
  name: string;
  properties?: Record<string, unknown>;
}

/**
 * Events mirrored into `journey_events` in addition to GA4.
 *
 * `trackEventInternal` only ever called `gtag` and `mixpanel`, so none of the
 * ~288 `trackEvent` call sites reached the database. Its only writer was
 * `trackUserAction`, and that is wired to auth, the marketing modal and link
 * sharing alone — which is why the 21 `user_action` types in production are
 * exactly those three areas and the product funnel is absent. See issue #303.
 *
 * This is a central map rather than a flag at each call site because journey
 * rows are durable storage: what lands there should be decided, and reviewed,
 * in one place. Keys are GA4 event names and are name-coupled, so
 * `analytics.funnel.test.ts` fails if an entry stops matching a live call site.
 *
 * The `action` is what journey queries group by, and it is not always the GA4
 * name. #303 asks for one `player_added` with a `method` discriminator; the two
 * code paths that add players are separate GA4 events, so both map onto that
 * one action and supply the discriminator here. GA4 keeps its own names either
 * way.
 *
 * Bidding is deliberately absent, but not for the reason first assumed here.
 * Measured 2026-09-08: bids run 19,753/30d against a `journey_events` baseline
 * of 102,220/30d, so mirroring them would add ~19% — comparable to the ~21k
 * rows this map already adds, not a flood. The real reasons are that
 * `bid_placed` and `auction_status_change` fire on the socket *listener* and so
 * write one row per connected client rather than one per action; and that a bid
 * happens far downstream of the create-to-setup drop #303 was opened to
 * explain, so it answers none of that question. Within the `user_action` bucket
 * specifically (9,322/30d today) bids would still be the majority. If bid-level
 * funnel data is wanted later, aggregate per auction rather than per bid.
 */
interface FunnelMapping {
  /** The `action` written to `journey_events` and grouped on by queries. */
  action: string;
  /** Static properties added to the journey row only, never to GA4. */
  with?: Record<string, unknown>;
}

const IDENTITY_FUNNEL_EVENTS = [
  // Onboarding — the first gate every Google signup meets, and the one the
  // 2026-09 audit measured a 33.6% drop at (393 reached the form, 261
  // finished). GA4 alone could not answer it: `error_fields` was never
  // registered as a custom dimension, so the failure reason was unqueryable
  // there. ~1,200 events/30d at current volume, against a table on a 30-day
  // TTL — negligible next to what it makes answerable.
  'onboarding_form_viewed',
  'onboarding_validation_error',
  'onboarding_submit_attempt',
  'onboarding_submit_success',
  // Create — where the audit measured a 64% drop with no way to explain it.
  // The three `tournament_create_*` events below are the screen's denominator
  // and its per-step funnel. They belong here for the same reason dashboard_view
  // does: their distinguishing properties (`layout`, `step`, `step_name`,
  // `direction`, `error_fields`) are not registered GA4 custom dimensions, so in
  // GA4 alone they collapse to a bare count — which is the one thing that does
  // not answer "which of the three wizard steps loses people".
  //
  // CAVEAT, measured 2026-09-12: membership of this list is currently inert.
  // journey_events has only ever stored `page_view`, `session_start` and
  // `user_action` — not one of the names below has produced a row, including on
  // 09-10/11/12, after the mirroring shipped in 950ca51e (09-08). The browser's
  // own `user_journey` localStorage buffer, which trackJourneyEvent writes to
  // before any network gate, shows the same three names only, so the break is at
  // the mapping/branch step rather than the batch POST. Registering here is
  // still correct — it is what makes these queryable the moment that is fixed —
  // but do not read it as "these are in the database".
  'tournament_create_view',
  'tournament_create_step_viewed',
  'tournament_create_step_blocked',
  'tournament_creation_attempt',
  'tournament_creation_validation_error',
  'tournament_creation_failure',
  'tournament_creation_success',
  // Setup — which step they land on, and whether they ever leave it.
  'tournament_setup_step_viewed',
  'tournament_setup_step_change',
  // Filling the tournament.
  'team_creation_success',
  'tournament_pool_create_success',
  // Paying for it.
  'tournament_payment_checkout_success',
  // The dashboard — the entry point in front of the whole funnel above, and
  // until 2026-09-11 the only major screen with no view event at all. Without
  // these two there is no denominator: /dashboard pageviews mix the brand-new
  // user and the returning organizer with nothing to tell them apart, so
  // "what % of new users who land here create a tournament?" was unanswerable
  // in GA4 *and* in the database.
  //
  // Volume: /dashboard saw 6,696 views / 1,803 sessions over the 28 days to
  // 2026-09-10 (GA4). These fire once per mount behind a ref guard, and that
  // view figure is itself inflated by a page_view double-count, so the real
  // rate is well under 6.7k/28d against a table on a 30-day TTL.
  'dashboard_view',
  'dashboard_empty_state_viewed',
  // Mirrored so `variant` lands in journey_events, where it is queryable by
  // SQL. In GA4 it is not a registered custom dimension, so 533 empty-state
  // CTA clicks over 28 days could not be split into create / tutorial /
  // browse_tutorials at all — the numerator was as unqueryable as the
  // denominator was absent.
  'dashboard_empty_state_cta_click',
  'dashboard_mock_auction_cta_click',
  // The next-action hub. `kind` and `target` are the whole point and neither is
  // a registered GA4 custom dimension, so these are mirrored to journey_events
  // where the properties survive and can be grouped by SQL. Without that we
  // could see that the block was clicked but never which of its ten states.
  'dashboard_next_action_click',
  'dashboard_dispatch_click',
] as const;

export const FUNNEL_EVENTS: ReadonlyMap<string, FunnelMapping> = new Map<
  string,
  FunnelMapping
>([
  ...IDENTITY_FUNNEL_EVENTS.map(
    (name) => [name, { action: name }] as [string, FunnelMapping]
  ),
  // Both player paths answer the same funnel question, so they share an action.
  [
    'player_registration_success',
    { action: 'player_added', with: { method: 'manual' } },
  ],
  [
    'bulk_player_upload_accepted',
    { action: 'player_added', with: { method: 'bulk' } },
  ],
  // Named for what it measures. #303 asks for `auction_started`, but this event
  // fires in PoolSelector before the try block that emits START_AUCTION, and
  // that emit has no ack — so it records an intent, not a start. The only
  // server-confirmed signal is the AUCTION_STATUS broadcast, which every client
  // in the room receives and which is the handler the 2026-05-27 CPU incident
  // traced to; mirroring from there would fan out and touch the hot path. A
  // real `auction_started` needs a backend ack on START_AUCTION.
  ['auction_admin_start_pool_click', { action: 'auction_start_requested' }],
]);

/**
 * Property keys dropped before an event is written to `journey_events`.
 *
 * GA4 events are ephemeral and aggregate; a journey row is a durable record in
 * our own database, so it should not carry identifiers for the person it
 * describes. `team_creation_success` used to send `owner_email` (removed
 * 2026-09-12 — the API never returned it, so it was always undefined) and
 * `bulk_player_upload_accepted` sends the uploaded `file_name`, which in
 * practice is often someone's name. The `owner_email` entry below stays as
 * defence in depth: nothing should be able to reintroduce it silently. Neither answers a funnel question — the
 * ids and counts alongside them do.
 *
 * This only redacts the database copy. That these are sent to GA4 at all is a
 * separate, pre-existing problem and is not changed here.
 */
const JOURNEY_REDACTED_KEYS: ReadonlySet<string> = new Set([
  'owner_email',
  'email',
  'user_email',
  'mobile_number',
  'mobileNumber',
  'file_name',
]);

/**
 * The journey row for an event, or null if it is not a funnel event.
 *
 * The shape matches `trackUserAction` — event `user_action`, name under
 * `action` — so the queries and dashboards already built on that table pick
 * these up with no schema change. The GA4 name is left untouched: routing
 * these through `trackUserAction` would have been the smaller edit but it
 * renames the event to `user_action_*`, flattening every existing report.
 */
export function funnelJourneyPayload(event: AnalyticsEvent): {
  event: string;
  data: Record<string, unknown>;
} | null {
  const mapping = FUNNEL_EVENTS.get(event.name);
  if (!mapping) return null;

  const data: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(event.properties ?? {})) {
    if (!JOURNEY_REDACTED_KEYS.has(key)) data[key] = value;
  }
  Object.assign(data, mapping.with);

  // Assigned last so it always wins: a funnel event carrying its own `action`
  // property would otherwise overwrite the one thing every query on this table
  // keys off.
  data.action = mapping.action;

  return { event: 'user_action', data };
}

interface GeoData {
  city?: string;
  region?: string;
  country?: string;
  country_code?: string;
  country_calling_code?: string;
  timezone?: string;
  latitude?: number;
  longitude?: number;
  postal_code?: string;
  language?: string;
  currency?: string;
  org?: string;
  isp?: string;
}

interface EngagementMetrics {
  timeOnPage: number;
  scrollDepth: number;
  lastInteraction: number;
  interactionCount: number;
  focusTime: number;
}

/**
 * Stable per-event id used to make journey ingestion idempotent.
 *
 * randomUUID is unavailable on http:// origins and older Safari, so fall back
 * to a random string — uniqueness is all this needs, not cryptographic
 * strength.
 */
function newClientEventId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

class Analytics {
  private static instance: Analytics;
  private initialized: boolean = false;
  private userId: string | null = null;
  private userContext: {
    userId?: string;
    userEmail?: string;
    userName?: string;
    userRole?: string;
  } | null = null;
  private geoData: GeoData | null = null;
  private sessionStartTime: number = Date.now();
  private lastActivityTime: number = Date.now();
  private engagementMetrics: EngagementMetrics = {
    timeOnPage: 0,
    scrollDepth: 0,
    lastInteraction: 0,
    interactionCount: 0,
    focusTime: 0,
  };
  private pageLoadTime: number | null = null;
  private isPageFocused: boolean = true;
  private maxScrollDepth: number = 0;
  private visitorId: string | null = null;
  private currentVisitId: string | null = null;
  private userJourney: Array<{
    timestamp: number;
    event: string;
    page: string;
    data?: Record<string, unknown>;
  }> = [];

  // Journey batching configuration
  private journeyEventQueue: Array<{
    clientEventId: string;
    timestamp: number;
    event: string;
    page: string;
    data?: Record<string, unknown>;
  }> = [];
  private batchTimeout: NodeJS.Timeout | null = null;
  private readonly BATCH_SIZE = 8; // Send after 8 events
  private readonly BATCH_TIMEOUT = 30000; // Send after 30 seconds

  // Geo data caching constants
  private static readonly GEO_CACHE_KEY = 'geo_data_cache';
  private static readonly GEO_CACHE_DURATION = 8 * 60 * 60 * 1000; // 8 hours in milliseconds

  private constructor() {
    if (typeof window !== 'undefined') {
      this.setupEngagementTracking();
    }
  }

  private setupEngagementTracking(): void {
    // Track page focus/blur
    window.addEventListener('focus', () => {
      this.isPageFocused = true;
      this.trackUserEngagement('focus');
    });

    window.addEventListener('blur', () => {
      this.isPageFocused = false;
      this.trackUserEngagement('blur');
    });

    // Track scroll depth
    window.addEventListener(
      'scroll',
      this.debounce(() => {
        const scrollDepth = this.calculateScrollDepth();
        if (scrollDepth > this.maxScrollDepth) {
          this.maxScrollDepth = scrollDepth;
          this.trackUserEngagement('scroll_depth', { depth: scrollDepth });
        }
      }, 500)
    );

    // Track user interactions
    window.addEventListener('click', () => this.trackInteraction('click'));
    window.addEventListener('keypress', () =>
      this.trackInteraction('keypress')
    );
    window.addEventListener(
      'mousemove',
      this.debounce(() => this.trackInteraction('mousemove'), 1000)
    );

    // Track page load performance
    if (window.performance) {
      window.addEventListener('load', () => {
        const perfData = window.performance.timing;
        const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;
        this.pageLoadTime = pageLoadTime;
        this.trackPerformanceMetrics();
      });
    }

    // Track before user leaves
    window.addEventListener('beforeunload', () => {
      this.trackSessionEnd();
    });
  }

  private debounce(
    func: (...args: unknown[]) => void,
    wait: number
  ): (...args: unknown[]) => void {
    let timeout: NodeJS.Timeout;
    return (...args: unknown[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  private calculateScrollDepth(): number {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return Math.round(((scrollTop + windowHeight) / documentHeight) * 100);
  }

  private trackInteraction(type: 'click' | 'keypress' | 'mousemove'): void {
    const now = Date.now();
    this.engagementMetrics.lastInteraction = now;
    this.engagementMetrics.interactionCount++;
    this.lastActivityTime = now;
    this.trackEvent({ name: 'user_interaction', properties: { type } });
  }

  private trackUserEngagement(
    action: string,
    details?: Record<string, unknown>
  ): void {
    this.trackEvent({
      name: `user_engagement_${action}`,
      properties: {
        ...details,
        time_since_last_activity: Date.now() - this.lastActivityTime,
        total_engagement_time: this.calculateEngagementTime(),
        interaction_count: this.engagementMetrics.interactionCount,
      },
    });
  }

  private calculateEngagementTime(): number {
    return this.isPageFocused ? Date.now() - this.sessionStartTime : 0;
  }

  private trackPerformanceMetrics(): void {
    if (!this.pageLoadTime) return;

    const metrics = {
      page_load_time: this.pageLoadTime,
      time_to_first_byte:
        window.performance.timing.responseStart -
        window.performance.timing.navigationStart,
      dom_load_time:
        window.performance.timing.domContentLoadedEventEnd -
        window.performance.timing.navigationStart,
      first_paint:
        window.performance.getEntriesByType('paint')[0]?.startTime || 0,
      first_contentful_paint:
        window.performance.getEntriesByType('paint')[1]?.startTime || 0,
    };

    this.trackEvent({
      name: 'performance_metrics',
      properties: metrics,
    });
  }

  private trackSessionEnd(): void {
    this.trackEvent({
      name: 'session_end',
      properties: {
        session_duration: Date.now() - this.sessionStartTime,
        max_scroll_depth: this.maxScrollDepth,
        total_interactions: this.engagementMetrics.interactionCount,
        engagement_time: this.calculateEngagementTime(),
      },
    });
  }

  static getInstance(): Analytics {
    if (!Analytics.instance) {
      Analytics.instance = new Analytics();
    }
    return Analytics.instance;
  }

  setUserId(id: string | null) {
    this.userId = id;
    if (window.gtag && id) {
      window.gtag('set', { user_id: id });
    }
  }

  async updateVisitorWithUserContext(): Promise<void> {
    if (!this.visitorId || !this.initialized) return;

    try {
      // Simply update session with user context - no complex journey tracking
      await visitorService.updateVisitorSession(this.visitorId, {
        userId: this.userContext?.userId,
        userEmail: this.userContext?.userEmail,
        userName: this.userContext?.userName,
        userRole: this.userContext?.userRole,
        userContextSetAt: Date.now(),
      });

      logger.debug('Visitor data updated with user context', {
        visitorId: this.visitorId,
        userId: this.userContext?.userId,
      });
    } catch (error) {
      logger.debug('Failed to update visitor with user context', { error });
    }
  }

  setUserContext(context: {
    userId?: string;
    userEmail?: string;
    userName?: string;
    userRole?: string;
  }) {
    const wasAnonymous = !this.userContext?.userId;
    this.userContext = context;

    // Only update visitor data when user context changes, no journey events
    if (this.initialized && wasAnonymous && context.userId) {
      this.updateVisitorWithUserContext();
    }
  }

  /**
   * Track user journey events with batching for efficiency
   */
  private trackJourneyEvent(
    event: string,
    data?: Record<string, unknown>
  ): void {
    const journeyEvent = {
      // Stable across retries of THIS event. flushBatchQueue re-queues a batch
      // whenever the request errors, including when it already committed
      // server-side (timeout, navigation abort, 5xx after the write). The
      // backend dedupes on this id, so a re-send is a no-op instead of a
      // duplicate row.
      clientEventId: newClientEventId(),
      timestamp: Date.now(),
      event,
      page: typeof window !== 'undefined' ? window.location.pathname : '',
      data,
    };

    // Add to in-memory journey
    this.userJourney.push(journeyEvent);

    // Keep only last 50 events to prevent memory issues
    if (this.userJourney.length > 50) {
      this.userJourney = this.userJourney.slice(-50);
    }

    // Store journey in localStorage for persistence across page loads
    try {
      const storedJourney = JSON.parse(
        localStorage.getItem('user_journey') || '[]'
      );
      storedJourney.push(journeyEvent);

      // Keep only last 100 events in storage
      const trimmedJourney = storedJourney.slice(-100);
      localStorage.setItem('user_journey', JSON.stringify(trimmedJourney));
    } catch (error) {
      logger.debug('Failed to store journey event', { error });
    }

    // Add to batch queue for backend sending
    if (this.visitorId && this.initialized) {
      this.addToBatchQueue(journeyEvent);
    }
  }

  /**
   * Add event to batch queue and manage batching logic
   */
  private addToBatchQueue(journeyEvent: {
    clientEventId: string;
    timestamp: number;
    event: string;
    page: string;
    data?: Record<string, unknown>;
  }): void {
    this.journeyEventQueue.push(journeyEvent);

    // Send immediately if batch size reached
    if (this.journeyEventQueue.length >= this.BATCH_SIZE) {
      this.flushBatchQueue();
      return;
    }

    // Set timeout to send batch if not already set
    if (!this.batchTimeout) {
      this.batchTimeout = setTimeout(() => {
        this.flushBatchQueue();
      }, this.BATCH_TIMEOUT);
    }
  }

  /**
   * Send batched journey events to backend
   */
  private async flushBatchQueue(): Promise<void> {
    if (!this.visitorId || this.journeyEventQueue.length === 0) return;

    // Clear timeout
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
      this.batchTimeout = null;
    }

    // Get events to send and clear queue
    const eventsToSend = [...this.journeyEventQueue];
    this.journeyEventQueue = [];

    try {
      await visitorService.trackJourneyEventsBatch(
        this.visitorId,
        eventsToSend
      );
      logger.debug('Journey events batch sent to backend', {
        visitorId: this.visitorId,
        eventCount: eventsToSend.length,
        events: eventsToSend.map((e) => e.event),
      });
    } catch (error) {
      logger.debug('Failed to send journey events batch to backend', { error });

      // Re-add failed events to queue for retry (but limit to prevent infinite growth)
      if (this.journeyEventQueue.length < this.BATCH_SIZE) {
        this.journeyEventQueue.unshift(...eventsToSend.slice(-this.BATCH_SIZE));
      }
    }
  }

  /**
   * Force flush any pending journey events (useful for page unload)
   */
  private async forceFlushJourneyEvents(): Promise<void> {
    if (this.journeyEventQueue.length > 0) {
      await this.flushBatchQueue();
    }
  }

  async init(): Promise<void> {
    if (this.initialized) return;

    // Initialize analytics services
    if (siteConfig.analytics.googleAnalyticsId) {
      // GA4 is now handled by @next/third-parties/google
      logger.debug('Google Analytics initialized');

      // Set default GA4 parameters
      if (window.gtag) {
        window.gtag('set', {
          allow_google_signals: true,
          allow_ad_personalization_signals: true,
        });
      }
    }

    if (process.env.NEXT_PUBLIC_MIXPANEL_TOKEN) {
      // Initialize Mixpanel
    }

    // Try to get geo data with caching
    this.geoData = await this.getCachedGeoData();

    // Load existing journey from storage
    try {
      const storedJourney = JSON.parse(
        localStorage.getItem('user_journey') || '[]'
      );
      this.userJourney = storedJourney.slice(-50); // Keep last 50 events in memory
    } catch (error) {
      logger.debug('Failed to load stored journey', { error });
    }

    // Track session start as simple journey event
    this.trackJourneyEvent('session_start', {
      sessionStartTime: this.sessionStartTime,
      referrer: typeof document !== 'undefined' ? document.referrer : undefined,
      entryPage: typeof window !== 'undefined' ? window.location.pathname : '',
    });

    // Track every visit (not just first-time) - ONLY ONCE
    await this.trackVisit();

    // Set up page unload handler to flush pending events
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        // Use sendBeacon for reliable delivery on page unload
        if (this.journeyEventQueue.length > 0 && this.visitorId) {
          const payload = JSON.stringify({
            visitorId: this.visitorId,
            events: this.journeyEventQueue,
          });

          try {
            navigator.sendBeacon(
              `${process.env.NEXT_PUBLIC_API_URL}/visitors/journey/batch`,
              payload
            );
          } catch (error) {
            logger.debug('Failed to send beacon on unload', { error });
          }
        }
      });
    }

    this.initialized = true;
  }

  /**
   * Get geo data with caching to reduce API calls
   */
  private async getCachedGeoData(): Promise<GeoData | null> {
    try {
      // Check if we have cached geo data
      const cachedData = localStorage.getItem(Analytics.GEO_CACHE_KEY);

      if (cachedData) {
        const { data, timestamp } = JSON.parse(cachedData);
        const now = Date.now();

        // Check if cached data is still valid (within 6 hours)
        if (now - timestamp < Analytics.GEO_CACHE_DURATION) {
          logger.debug('Using cached geo data', {
            cachedAt: new Date(timestamp).toISOString(),
            expiresAt: new Date(
              timestamp + Analytics.GEO_CACHE_DURATION
            ).toISOString(),
          });
          return data;
        } else {
          logger.debug('Cached geo data expired, fetching fresh data');
        }
      }

      // Fetch fresh geo data
      logger.debug('Fetching fresh geo data from ipapi.co');
      const response = await fetch('https://ipapi.co/json/');
      const geoData = await response.json();

      // Cache the fresh data with timestamp
      const cacheEntry = {
        data: geoData,
        timestamp: Date.now(),
      };
      localStorage.setItem(Analytics.GEO_CACHE_KEY, JSON.stringify(cacheEntry));

      logger.debug('Geo data cached successfully', {
        cachedAt: new Date().toISOString(),
        expiresAt: new Date(
          Date.now() + Analytics.GEO_CACHE_DURATION
        ).toISOString(),
      });

      return geoData;
    } catch (error) {
      logger.debug('Failed to fetch geo data', { error });

      // Try to return stale cached data if available
      const cachedData = localStorage.getItem(Analytics.GEO_CACHE_KEY);
      if (cachedData) {
        const { data } = JSON.parse(cachedData);
        logger.debug('Using stale cached geo data due to fetch error');
        return data;
      }

      return null;
    }
  }

  private async trackVisit(): Promise<void> {
    try {
      // Get or create visitor ID
      let visitorId = localStorage.getItem('visitor_id');
      if (!visitorId) {
        visitorId = this.generateVisitorId();
        localStorage.setItem('visitor_id', visitorId);
      }
      this.visitorId = visitorId;

      // Track this visit
      const visitorData = await this.collectComprehensiveVisitorData();

      try {
        const response = await visitorService.trackVisitor(visitorData);

        if (response.success) {
          this.currentVisitId = response.visitId || null;

          // Update visit count
          const visitCount =
            parseInt(localStorage.getItem('visit_count') || '0') + 1;
          localStorage.setItem('visit_count', visitCount.toString());
          localStorage.setItem('last_visit_date', new Date().toISOString());

          logger.debug('Visit tracked successfully', {
            visitorId: response.visitorId,
            visitId: response.visitId,
            visitNumber: visitCount,
          });

          // Set up periodic session updates
          this.setupSessionTracking();
        }
      } catch (error) {
        logger.error('Failed to track visit', error as Error);
      }
    } catch (error) {
      logger.error('Error in trackVisit', error as Error);
    }
  }

  private setupSessionTracking(): void {
    // Update session data every 30 seconds
    setInterval(() => {
      if (this.visitorId && this.currentVisitId) {
        this.updateSessionData();
      }
    }, 30000);

    // Update on page unload
    window.addEventListener('beforeunload', () => {
      if (this.visitorId && this.currentVisitId) {
        this.updateSessionData();
      }
    });
  }

  private async updateSessionData(): Promise<void> {
    if (!this.visitorId) return;

    try {
      await visitorService.updateVisitorSession(this.visitorId, {
        timeOnPage: Date.now() - this.sessionStartTime,
        scrollDepth: this.maxScrollDepth,
        interactionCount: this.engagementMetrics.interactionCount,
      });
    } catch (error) {
      logger.debug('Failed to update session data', { error });
    }
  }

  private async collectComprehensiveVisitorData(): Promise<VisitorData> {
    // Generate unique visitor ID if needed
    const visitorId = this.visitorId || this.generateVisitorId();
    const sessionId = this.getSessionId();

    // Get visit count
    const visitCount = parseInt(localStorage.getItem('visit_count') || '0') + 1;
    const lastVisitDate = localStorage.getItem('last_visit_date');
    const firstVisitDate =
      localStorage.getItem('first_visit_date') || new Date().toISOString();

    if (!localStorage.getItem('first_visit_date')) {
      localStorage.setItem('first_visit_date', firstVisitDate);
    }

    // Get GA Client ID if available
    let gaClientId: string | undefined;
    let gaSessionId: string | undefined;
    if (window.gtag) {
      try {
        window.gtag(
          'get',
          siteConfig.analytics.googleAnalyticsId,
          'client_id',
          (clientId: string) => {
            gaClientId = clientId;
          }
        );
        window.gtag(
          'get',
          siteConfig.analytics.googleAnalyticsId,
          'session_id',
          (sessionId: string) => {
            gaSessionId = sessionId;
          }
        );
      } catch (error) {
        logger.debug('Failed to get GA IDs', { error });
      }
    }

    // Get detailed browser and device info
    const browserInfo = this.getDetailedBrowserInfo();
    const deviceCapabilities = this.getDeviceCapabilities();
    const performanceMetrics = this.getPerformanceMetrics();

    return {
      visitorId,
      sessionId,
      timestamp: new Date().toISOString(),
      visitNumber: visitCount,

      // User context (if logged in)
      userId: this.userContext?.userId,
      userEmail: this.userContext?.userEmail,
      userName: this.userContext?.userName,
      userRole: this.userContext?.userRole,

      // Location data from geo API
      city: this.geoData?.city,
      region: this.geoData?.region,
      country: this.geoData?.country,
      countryCode: this.geoData?.country_code,
      callingCode: this.geoData?.country_calling_code,
      timezone: this.geoData?.timezone,
      latitude: this.geoData?.latitude,
      longitude: this.geoData?.longitude,
      postalCode: this.geoData?.postal_code,
      isp: this.geoData?.isp,
      organization: this.geoData?.org,

      // Enhanced Device & Browser info
      userAgent: navigator.userAgent,
      deviceType: this.getDeviceType(),
      browser: browserInfo.browser,
      browserVersion: browserInfo.version,
      operatingSystem: browserInfo.os,
      osVersion: browserInfo.osVersion,
      screenResolution: `${screen.width}x${screen.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
      colorDepth: screen.colorDepth,
      pixelRatio: window.devicePixelRatio,
      touchSupport: 'ontouchstart' in window,
      cookieEnabled: navigator.cookieEnabled,
      javaEnabled: navigator.javaEnabled?.() || false,

      // Network info
      connectionType: this.getConnectionInfo().type,
      connectionSpeed: this.getConnectionInfo().effectiveType,
      connectionDownlink: this.getConnectionInfo().downlink,
      connectionRtt: this.getConnectionInfo().rtt,
      onlineStatus: navigator.onLine,

      // Page context
      pageUrl: window.location.href,
      pagePath: window.location.pathname,
      pageTitle: document.title,
      referrer: document.referrer || undefined,
      referrerDomain: document.referrer
        ? new URL(document.referrer).hostname
        : undefined,
      language: navigator.language,
      languages: navigator.languages
        ? Array.from(navigator.languages)
        : undefined,

      // UTM & Campaign data
      utmSource: this.getUtmParam('utm_source') || undefined,
      utmMedium: this.getUtmParam('utm_medium') || undefined,
      utmCampaign: this.getUtmParam('utm_campaign') || undefined,
      utmTerm: this.getUtmParam('utm_term') || undefined,
      utmContent: this.getUtmParam('utm_content') || undefined,

      // Additional tracking IDs
      gaClientId,
      gaSessionId,
      fbClickId: this.getUtmParam('fbclid') || undefined,
      gclid: this.getUtmParam('gclid') || undefined,

      // Performance metrics
      ...performanceMetrics,

      // Session & Engagement data
      sessionStartTime: this.sessionStartTime,
      timeOnPage: 0, // Will be updated via session tracking
      scrollDepth: 0, // Will be updated via session tracking
      interactionCount: 0, // Will be updated via session tracking

      // Technical capabilities
      ...deviceCapabilities,

      // Additional browser capabilities
      doNotTrack: navigator.doNotTrack === '1',
      adBlocker: this.detectAdBlocker(),
      plugins: this.getPluginsList(),

      // Visit metadata
      isReturningVisitor: visitCount > 1,
      daysSinceFirstVisit: this.daysSinceFirstVisit(),
      totalVisits: visitCount,
      lastVisitDate,
    };
  }

  private getDeviceCapabilities(): {
    webglSupport?: boolean;
    webrtcSupport?: boolean;
    localStorageSupport?: boolean;
    sessionStorageSupport?: boolean;
    indexedDbSupport?: boolean;
  } {
    return {
      webglSupport: this.hasWebGLSupport(),
      webrtcSupport: this.hasWebRTCSupport(),
      localStorageSupport: this.hasLocalStorageSupport(),
      sessionStorageSupport: this.hasSessionStorageSupport(),
      indexedDbSupport: this.hasIndexedDBSupport(),
    };
  }

  private getPerformanceMetrics(): {
    pageLoadTime?: number;
    domLoadTime?: number;
    firstPaintTime?: number;
    firstContentfulPaintTime?: number;
  } {
    if (!window.performance) return {};

    const timing = window.performance.timing;
    const paintEntries = window.performance.getEntriesByType('paint');

    return {
      pageLoadTime: timing.loadEventEnd - timing.navigationStart,
      domLoadTime: timing.domContentLoadedEventEnd - timing.navigationStart,
      firstPaintTime: paintEntries.find((entry) => entry.name === 'first-paint')
        ?.startTime,
      firstContentfulPaintTime: paintEntries.find(
        (entry) => entry.name === 'first-contentful-paint'
      )?.startTime,
    };
  }

  private getDetailedBrowserInfo(): {
    browser: string;
    version?: string;
    os?: string;
    osVersion?: string;
  } {
    const ua = navigator.userAgent;

    // Browser detection with version
    let browser = 'unknown';
    let version: string | undefined;

    if (ua.includes('Chrome')) {
      browser = 'chrome';
      version = ua.match(/Chrome\/([0-9.]+)/)?.[1];
    } else if (ua.includes('Firefox')) {
      browser = 'firefox';
      version = ua.match(/Firefox\/([0-9.]+)/)?.[1];
    } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
      browser = 'safari';
      version = ua.match(/Version\/([0-9.]+)/)?.[1];
    } else if (ua.includes('Edge')) {
      browser = 'edge';
      version = ua.match(/Edge\/([0-9.]+)/)?.[1];
    } else if (ua.includes('Opera')) {
      browser = 'opera';
      version = ua.match(/Opera\/([0-9.]+)/)?.[1];
    }

    // Enhanced OS detection with version
    let os = 'unknown';
    let osVersion: string | undefined;

    if (ua.includes('Windows NT')) {
      os = 'windows';
      osVersion = ua.match(/Windows NT ([0-9.]+)/)?.[1];
    } else if (ua.includes('Mac OS X')) {
      os = 'macos';
      osVersion = ua.match(/Mac OS X ([0-9_]+)/)?.[1]?.replace(/_/g, '.');
    } else if (ua.includes('Linux')) {
      os = 'linux';
    } else if (ua.includes('Android')) {
      os = 'android';
      osVersion = ua.match(/Android ([0-9.]+)/)?.[1];
    } else if (ua.includes('iPhone OS') || ua.includes('iOS')) {
      os = 'ios';
      osVersion = ua.match(/OS ([0-9_]+)/)?.[1]?.replace(/_/g, '.');
    }

    return { browser, version, os, osVersion };
  }

  // Helper methods for capability detection
  private hasWebGLSupport(): boolean {
    try {
      const canvas = document.createElement('canvas');
      return !!(
        canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
      );
    } catch {
      return false;
    }
  }

  private hasWebRTCSupport(): boolean {
    return !!(
      window.RTCPeerConnection ||
      window.webkitRTCPeerConnection ||
      window.mozRTCPeerConnection
    );
  }

  private hasLocalStorageSupport(): boolean {
    try {
      localStorage.setItem('test', 'test');
      localStorage.removeItem('test');
      return true;
    } catch {
      return false;
    }
  }

  private hasSessionStorageSupport(): boolean {
    try {
      sessionStorage.setItem('test', 'test');
      sessionStorage.removeItem('test');
      return true;
    } catch {
      return false;
    }
  }

  private hasIndexedDBSupport(): boolean {
    return !!(
      window.indexedDB ||
      window.webkitIndexedDB ||
      window.mozIndexedDB
    );
  }

  private detectAdBlocker(): boolean {
    // Simple ad blocker detection
    try {
      const testAd = document.createElement('div');
      testAd.innerHTML = '&nbsp;';
      testAd.className = 'adsbox';
      testAd.style.position = 'absolute';
      testAd.style.left = '-10000px';
      document.body.appendChild(testAd);
      const isBlocked = testAd.offsetHeight === 0;
      document.body.removeChild(testAd);
      return isBlocked;
    } catch {
      return false;
    }
  }

  private getPluginsList(): string[] {
    try {
      return Array.from(navigator.plugins).map((plugin) => plugin.name);
    } catch {
      return [];
    }
  }

  private generateVisitorId(): string {
    return (
      'visitor_' + Math.random().toString(36).substring(2) + '_' + Date.now()
    );
  }

  private getDeviceType(): 'mobile' | 'desktop' | 'tablet' {
    const ua = navigator.userAgent;
    if (/tablet|ipad|playbook|silk/i.test(ua)) {
      return 'tablet';
    }
    if (
      /mobile|android|iphone|ipod|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(
        ua
      )
    ) {
      return 'mobile';
    }
    return 'desktop';
  }

  private getConnectionInfo(): {
    type?: string;
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
  } {
    const connection =
      (
        navigator as unknown as {
          connection?: NetworkInformation;
          mozConnection?: NetworkInformation;
          webkitConnection?: NetworkInformation;
        }
      ).connection ||
      (
        navigator as unknown as {
          connection?: NetworkInformation;
          mozConnection?: NetworkInformation;
          webkitConnection?: NetworkInformation;
        }
      ).mozConnection ||
      (
        navigator as unknown as {
          connection?: NetworkInformation;
          webkitConnection?: NetworkInformation;
        }
      ).webkitConnection;

    if (!connection) return {};

    return {
      type: connection.type,
      effectiveType: connection.effectiveType,
      downlink: connection.downlink,
      rtt: connection.rtt,
    };
  }

  private enrichEventProperties(
    properties?: Record<string, unknown>
  ): Record<string, unknown> {
    // GA4 reserves the bare param names `source`, `medium`, `campaign`, `term`,
    // and `content` as event-level traffic-source overrides. Forwarding them as
    // event params reassigns the session's acquisition channel and dumps real
    // Organic/Direct traffic into "Unassigned". Callers use `source` as a
    // UI-origin label (e.g. 'header_cta'), so remap the reserved keys to safe
    // `cta_*` params that keep the data without hijacking attribution.
    const { source, medium, campaign, term, content, ...safeProperties } =
      properties ?? {};
    const remappedReservedParams: Record<string, unknown> = {};
    if (source !== undefined) remappedReservedParams.cta_source = source;
    if (medium !== undefined) remappedReservedParams.cta_medium = medium;
    if (campaign !== undefined) remappedReservedParams.cta_campaign = campaign;
    if (term !== undefined) remappedReservedParams.cta_term = term;
    if (content !== undefined) remappedReservedParams.cta_content = content;

    const enriched = {
      ...safeProperties,
      ...remappedReservedParams,
      ...buildEnrichmentParams({
        userId: this.userId,
        appVersion: process.env.NEXT_PUBLIC_APP_VERSION,
        userSegment: this.getUserSegment(),
        isReturningUser: this.isReturningUser(),
        daysSinceFirstVisit: this.daysSinceFirstVisit(),
        scrollDepth: this.maxScrollDepth,
      }),
    };

    // GA4 truncates past the cap silently, so a caller that quietly grows its
    // property list would start losing dimensions with no signal at all. Say so
    // in development rather than discovering it months later in a (not set).
    if (
      process.env.NODE_ENV !== 'production' &&
      Object.keys(enriched).length > GA4_MAX_EVENT_PARAMS
    ) {
      logger.warn(
        `Analytics event exceeds the GA4 ${GA4_MAX_EVENT_PARAMS}-parameter cap ` +
          `(${Object.keys(enriched).length}); GA4 will drop some at random.`,
        { keys: Object.keys(enriched) }
      );
    }

    return enriched;
  }

  private isReturningUser(): boolean {
    const firstVisit = localStorage.getItem('first_visit');
    if (!firstVisit) {
      localStorage.setItem('first_visit', Date.now().toString());
      return false;
    }
    return true;
  }

  private daysSinceFirstVisit(): number {
    const firstVisit = localStorage.getItem('first_visit');
    if (!firstVisit) return 0;
    const days = (Date.now() - parseInt(firstVisit)) / (1000 * 60 * 60 * 24);
    return Math.floor(days);
  }

  private getUserSegment(): string {
    const visitCount = parseInt(localStorage.getItem('visit_count') || '0') + 1;
    localStorage.setItem('visit_count', visitCount.toString());

    if (visitCount === 1) return 'new';
    if (visitCount < 5) return 'occasional';
    if (visitCount < 10) return 'regular';
    return 'loyal';
  }

  private getUtmParam(param: string): string | null {
    if (typeof window === 'undefined') return null;
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  }

  private getSessionId(): string {
    if (typeof window === 'undefined') return '';
    let sessionId = sessionStorage.getItem('analytics_session_id');
    if (!sessionId) {
      sessionId = Math.random().toString(36).substring(2);
      sessionStorage.setItem('analytics_session_id', sessionId);
    }
    return sessionId;
  }

  trackEvent(event: AnalyticsEvent): void {
    try {
      // Ensure analytics is initialized before tracking any event
      if (!this.initialized) {
        // Initialize analytics immediately and track the event after initialization
        this.init()
          .then(() => {
            this.trackEventInternal(event);
          })
          .catch((error) => {
            logger.error(
              'Failed to initialize analytics before tracking event',
              error as Error,
              { event }
            );
          });
        return;
      }

      this.trackEventInternal(event);
    } catch (error) {
      logger.error('Failed to track analytics event', error as Error, {
        event,
      });
    }
  }

  private trackEventInternal(event: AnalyticsEvent): void {
    const enrichedProperties = this.enrichEventProperties(event.properties);

    // Before the sinks below, so a throwing gtag cannot cost the database row.
    // Raw properties, not enriched: `enrichEventProperties` exists to shape a
    // GA4 hit — it stamps the handful of dimensions GA4 cannot derive (user_id,
    // app_version, user_segment, is_returning_user, days_since_first_visit,
    // scroll_depth) and remaps the GA4-reserved param names to `cta_*`. A
    // journey row needs none of that (the batch already carries visitor and
    // page), and rewriting keys for GA4's sake would make the two sinks
    // disagree about what a property is called.
    const journeyPayload = funnelJourneyPayload(event);
    if (journeyPayload) {
      this.trackJourneyEvent(journeyPayload.event, journeyPayload.data);
    }

    if (window.gtag) {
      window.gtag('event', event.name, enrichedProperties);
    }

    if (window.mixpanel) {
      window.mixpanel.track(event.name, enrichedProperties);
    }

    logger.debug('Analytics event tracked', {
      event: { ...event, enrichedProperties },
    });
  }

  trackPageView(path: string): void {
    // Ensure analytics is initialized before tracking page view
    if (!this.initialized) {
      this.init()
        .then(() => {
          this.trackPageViewInternal(path);
        })
        .catch((error) => {
          logger.error(
            'Failed to initialize analytics before tracking page view',
            error as Error,
            { path }
          );
        });
      return;
    }

    this.trackPageViewInternal(path);
  }

  private trackPageViewInternal(path: string): void {
    // Track page view as simple journey event (no API call)
    this.trackJourneyEvent('page_view', {
      path,
      referrer: typeof document !== 'undefined' ? document.referrer : undefined,
      timestamp: Date.now(),
    });

    // Track normal analytics event
    this.trackEvent({
      name: 'page_view',
      properties: {
        path,
        referrer:
          typeof document !== 'undefined' ? document.referrer : undefined,
        journey_length: this.userJourney.length,
      },
    });
  }

  trackError(error: Error, context?: Record<string, unknown>): void {
    this.trackEvent({
      name: 'error',
      properties: {
        message: error.message,
        stack: error.stack,
        ...context,
      },
    });
  }

  trackPerformance(metric: {
    name: string;
    value: number;
    label?: string;
  }): void {
    this.trackEvent({
      name: 'performance_metric',
      properties: metric,
    });
  }

  // Custom event tracking methods with simple journey tracking
  trackUserAction(action: string, details?: Record<string, unknown>): void {
    // Track as simple journey event (no API call)
    this.trackJourneyEvent('user_action', {
      action,
      ...details,
    });

    // Track normal analytics event
    this.trackEvent({
      name: `user_action_${action}`,
      properties: {
        ...details,
        journey_length: this.userJourney.length,
      },
    });
  }

  trackAuctionEvent(
    action: string,
    auctionDetails: Record<string, unknown>
  ): void {
    this.trackEvent({
      name: `auction_${action}`,
      properties: auctionDetails,
    });
  }

  trackBidEvent(bidDetails: Record<string, unknown>): void {
    this.trackEvent({
      name: 'bid_placed',
      properties: bidDetails,
    });
  }
}

export const analytics = Analytics.getInstance();
